import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { BRANDS } from "@/data/brands";
import { useT } from "@/i18n/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Wide-tracked title utility — kept for other sections that already use it.
export function Spaced({ text, className = "" }) {
  const words = text.split(/\s+/);
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={`${w}-${i}`} className="inline-block whitespace-nowrap">
          {w.split("").map((ch, j) => (
            <span key={`${w}-${i}-${j}`} style={{ letterSpacing: "0.28em" }}>
              {ch}
            </span>
          ))}
          {i < words.length - 1 && (
            <span style={{ display: "inline-block", width: "0.35em" }} />
          )}
        </span>
      ))}
    </span>
  );
}

// A splash of colored dots — subtle brand palette confetti behind the type
function ColorDots() {
  const dots = [
    { c: "#25bfcf", x: "24%", y: "20%", s: 14 },
    { c: "#FFD53F", x: "88%", y: "15%", s: 22 },
    { c: "#E4004E", x: "12%", y: "78%", s: 10 },
    { c: "#FF7A2E", x: "82%", y: "72%", s: 16 },
    { c: "#0B1C4B", x: "48%", y: "8%", s: 8 },
    { c: "#25bfcf", x: "72%", y: "40%", s: 6 },
  ];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d, i) => (
        <motion.span
          key={`${d.c}-${d.x}-${d.y}`}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.4 + i * 0.12 }}
          className="absolute rounded-full"
          style={{
            left: d.x,
            top: d.y,
            width: `${d.s}px`,
            height: `${d.s}px`,
            background: d.c,
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const { t } = useT();
  return (
    <section
      id="hero"
      data-testid={HERO.root}
      className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #FBF6EC 0%, #F3ECDF 100%)" }}
    >
      <ColorDots />
      <img
        aria-hidden
        src="/hupla-emblem.svg"
        alt=""
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[780px] max-w-none opacity-[0.04]"
      />

      <div className="relative mx-auto max-w-[1500px] px-6 lg:px-12">
        {/* Small pre-title strip */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-16 bg-[#001F39]" />
          <span
            className="text-[0.7rem] font-bold uppercase text-[#001F39]"
            style={{ letterSpacing: "0.34em" }}
          >
            A Hupla Story · Est. Türkiye
          </span>
        </motion.div>

        {/* Grid — left type, right product hero */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          <div className="lg:col-span-5 relative z-20 min-w-0 lg:pr-12 xl:pr-14" style={{ overflowWrap: "break-word", hyphens: "auto" }}>
            {/* Oversized editorial serif statement */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="font-display font-black text-[#001F39] text-[clamp(2.25rem,4vw,4.5rem)] leading-[0.9] tracking-tight"
            >
              {t("hero.the")}
              {t("hero.tasteOf") && (<><br />{t("hero.tasteOf")}</>)}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 40, rotate: -1 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="font-editorial italic text-[clamp(2.5rem,5vw,5.25rem)] leading-[0.9] mt-2 relative"
              style={{ color: "#25bfcf" }}
            >
              {t("hero.living")}
              {t("hero.well") && (
                <>
                  <br />
                  <span className="relative inline-block">
                    {t("hero.well")}
                    <span
                      aria-hidden
                      className="absolute -bottom-3 left-0 right-0 h-2 rounded-full"
                      style={{ background: "#FFD53F" }}
                    />
                  </span>
                </>
              )}
              {!t("hero.well") && (
                <span
                  aria-hidden
                  className="absolute -bottom-3 left-0 h-2 rounded-full"
                  style={{ background: "#FFD53F", width: "60%" }}
                />
              )}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-8 text-base lg:text-lg text-[#001F39]/75 leading-relaxed"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-8 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3"
            >
              <button
                data-testid={HERO.ctaBrands}
                onClick={() => scrollTo("brands")}
                className="group inline-flex items-center gap-3 rounded-full bg-[#001F39] text-white pl-6 pr-2 py-2 text-sm font-medium hover:bg-[#25bfcf] transition-all"
              >
                {t("hero.exploreBrands")}
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#25bfcf] text-[#001F39] transition-transform group-hover:translate-y-0.5">
                  <ArrowDown className="w-4 h-4" />
                </span>
              </button>
              <button
                data-testid={HERO.ctaB2B}
                onClick={() => scrollTo("b2b")}
                className="group inline-flex items-center gap-2 rounded-full border-2 border-[#001F39] pl-5 pr-4 py-2.5 text-sm font-medium text-[#001F39] hover:bg-[#001F39] hover:text-white transition-all"
              >
                {t("hero.talkTrade")}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
              </button>
            </motion.div>

            {/* Brand accent chips row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              className="mt-10 flex flex-wrap gap-1.5"
            >
              {BRANDS.map((b, i) => (
                <span
                  key={b.slug}
                  className="inline-block h-6 rounded-full"
                  style={{
                    width: `${28 + (i % 3) * 8}px`,
                    background: b.palette.accent,
                  }}
                  title={b.name}
                />
              ))}
              <span className="ml-3 self-center overline text-[#001F39]/70">
                our brands
              </span>
            </motion.div>
          </div>

          {/* Right: curated product hero — individual product photos, no composite */}
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 relative"
          >
            {/* Decorative sunburst behind */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span
                className="block w-[110%] aspect-square rounded-full blur-3xl opacity-70"
                style={{
                  background:
                    "radial-gradient(circle, #FFEBB1 0%, transparent 60%)",
                }}
              />
            </div>
            {/* Real-world-proportional row: heights scale with each product's physical mm.
                Preferred vw factor kept conservative and row extended into the right gutter
                so Desser (tallest, on the far right) remains fully visible on all viewports. */}
            <div className="relative flex items-end justify-start gap-2 md:gap-3 flex-nowrap lg:-mr-4 xl:-mr-8">
              {BRANDS.slice(0, 7).map((b, i) => (
                <motion.img
                  key={b.slug}
                  src={b.frontImage || b.image}
                  alt={b.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.6 + i * 0.08 }}
                  className="w-auto object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.18)]"
                  style={{
                    height: `clamp(${(b.heightMm || 200) * 0.85}px, ${(b.heightMm || 200) * 0.088}vw, ${(b.heightMm || 200) * 1.7}px)`,
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom marquee ribbon */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 lg:mt-24 -mx-6 lg:-mx-12 border-y-2 border-[#001F39] py-4 overflow-hidden"
          style={{ background: "#001F39" }}
        >
          <div className="marquee-track flex items-center gap-5 whitespace-nowrap">
            {[
              "energy drinks",
              "kids' fruit juice",
              "malt beverages",
              "premium tonics",
              "iced coffee",
              "aperitifs",
              "dessert sauces",
              "food & drink, crafted",
            ]
              .concat([
                "energy drinks",
                "kids' fruit juice",
                "malt beverages",
                "premium tonics",
                "iced coffee",
                "aperitifs",
                "dessert sauces",
                "food & drink, crafted",
              ])
              .map((word, i) => (
                <React.Fragment key={`marquee-${word}-${i}`}>
                  <span className="text-white text-2xl md:text-3xl font-editorial italic">
                    {word}
                  </span>
                  <svg
                    aria-hidden
                    viewBox="0 0 74.27 75.18"
                    className="w-5 h-5 md:w-6 md:h-6 text-[#25bfcf] flex-shrink-0"
                    fill="currentColor"
                  >
                    <path d="M69.09 27.07l-6.28-4.81V13.2C62.8 5.92 56.89 0 49.61 0c-1.49 0-2.96.25-4.37.75l-8.09 2.83L29.06.75C27.64.25 26.17 0 24.69 0a13.12 13.12 0 0 0-9.22 3.76c-2.57 2.5-3.98 5.86-3.98 9.45v9.06L5.18 27.1C1.88 29.62 0 33.44 0 37.59s1.9 7.97 5.2 10.49l6.29 4.8v8.83c0 7.28 5.92 13.2 13.19 13.2 1.49 0 2.96-.25 4.37-.75l8.09-2.83 8.09 2.83c1.41.49 2.88.75 4.37.75 7.28 0 13.19-5.92 13.19-13.2v-8.94l6.19-4.65a13.1 13.1 0 0 0 5.27-10.49 13.08 13.08 0 0 0-5.18-10.54zm-5.62 13.68l-7.27 5.46c-1.63 1.23-2.6 3.15-2.6 5.2V61.7c0 2.29-1.87 4-4 4a3.92 3.92 0 0 1-1.33-.23l-8.98-3.15a6.61 6.61 0 0 0-2.15-.37c-.73 0-1.45.12-2.15.37l-8.98 3.15a3.88 3.88 0 0 1-1.33.23c-2.12 0-4-1.72-4-4V51.54a6.51 6.51 0 0 0-2.56-5.17l-7.36-5.61a4 4 0 0 1 0-6.36l7.37-5.64c1.61-1.23 2.55-3.14 2.55-5.16V13.21c0-2.29 1.87-4 4-4a3.92 3.92 0 0 1 1.33.23l8.98 3.15a6.61 6.61 0 0 0 2.15.37c.73 0 1.45-.12 2.15-.37l8.98-3.15a3.88 3.88 0 0 1 1.33-.23c2.12 0 4 1.72 4 4V23.6c0 2.02.94 3.93 2.55 5.16l7.34 5.62a4.01 4.01 0 0 1-.03 6.38z" />
                  </svg>
                </React.Fragment>
              ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
